// Each tech's week: weather, week navigation, the week grid (#week-container) with drag and drop,
// every tech's available hours (globalTechStats / globalTechList, also read by haulLaunch.js through
// window.parent.globalTechList), and the tech-assign window an op row's tech button opens.
// Split out of app.js (docs/split-plan.md, session 4). The app.js helpers it calls come in through
// setTechWeekDeps() (see estimates.js for why it doesn't import app.js back).
import { parseDay } from './date-format';
import { database } from './initFirebase';
import { marinaLatLng } from './marinaLocation';
import { techLabel } from './techNames';
import { update } from './trackedDb';
import { escapeHTML, toastMessage, updateTimestamps, woLabel } from './utilities';
import { splitTechs, techAvatar } from './wo-ops-aggregation';
import { Sort, getJobDataIndex } from './woFilters';
import { get, ref } from 'firebase/database';

let shell = null;
export function setTechWeekDeps(deps) { shell = deps; }

// Every tech's available hours this week, worked out by updateWeekView(); app.js reads globalTechStats.
let globalTechList = [];
export let globalTechStats;

const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const month = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];


async function fetchWeatherInfo(dateString) {
  if (!navigator.onLine) {
    console.log('User is offline. Skipping weather info fetch.');
    return null;
  }

/*   const location = await setupLocation(); */

  const ll = await marinaLatLng();
  const location = ll && { latitude: ll.lat, longitude: ll.lng };
  if (!location) {
    console.warn('No marina location set (Settings › Yard profile). Weather info cannot be fetched.');
    return null;
  }

  const { latitude, longitude } = location;

  const apiKeySnapshot = await get(ref(database, 'API/openWeatherKey'));
  const apiKey = apiKeySnapshot.val();
  if (!apiKey) {
    console.error('OpenWeatherMap API key not found at API/openWeatherKey in Firebase');
    return null;
  }

  // Use UTC dates for comparison
  const targetDate = new Date(dateString + 'T00:00:00Z');
  const today = new Date();
  const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));

  // Compare dates using UTC
  const isToday = targetDate.getTime() === todayUTC.getTime();
  const daysDifference = Math.floor((targetDate - todayUTC) / (1000 * 60 * 60 * 24));

  try {
    if (isToday || daysDifference === 0) {
      // Fetch current weather for today
      const currentWeatherEndpoint = `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${apiKey}&units=imperial`;
      const response = await fetch(currentWeatherEndpoint);
      const weatherData = await response.json();
      return {
        temperature: Math.round(weatherData.main.temp_max),
        description: weatherData.weather[0].description,
        windSpeed: Math.round(weatherData.wind.speed),
        windDirection: getWindDirection(weatherData.wind.deg),
      };
    } else if (daysDifference > 0 && daysDifference <= 5) {
      // Fetch forecast for next 5 days
      const forecastEndpoint = `https://api.openweathermap.org/data/2.5/forecast?lat=${latitude}&lon=${longitude}&appid=${apiKey}&units=imperial`;
      const response = await fetch(forecastEndpoint);
      const data = await response.json();

      // Find the forecast for the specified date
      const forecast = data.list.find(item => {
        const itemDate = new Date(item.dt * 1000);
        return itemDate.toDateString() === targetDate.toDateString();
      });

      if (forecast) {
        return {
          temperature: Math.round(forecast.main.temp),
          description: forecast.weather[0].description,
          windSpeed: Math.round(forecast.wind.speed),
          windDirection: getWindDirection(forecast.wind.deg),
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch weather data:', error);
    return null;
  }
}

function getWeatherEmoji(description) {
  const lowerDesc = description.toLowerCase();
  if (lowerDesc.includes('clear')) return '☀️';
  if (lowerDesc.includes('cloud')) return '☁️';
  if (lowerDesc.includes('rain')) return '🌧️';
  if (lowerDesc.includes('snow')) return '❄️';
  if (lowerDesc.includes('thunderstorm')) return '⛈️';
  if (lowerDesc.includes('mist') || lowerDesc.includes('fog')) return '🌫️';
  return '🌤️'; // default to partly cloudy
}

function getWindDirection(degrees) {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(degrees / 45) % 8];
}

let currentWeekStart;

export function initializeWeekStart() {
  currentWeekStart = new Date();
  let dayOfWeek = currentWeekStart.getDay();
  let diff = (dayOfWeek + 6) % 7;
  currentWeekStart.setDate(currentWeekStart.getDate() - diff);
  currentWeekStart.setHours(0, 0, 0, 0);
}

function navigateWeek(direction) {
  currentWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
  updateWeekView();
}

document.getElementById('scheduleButton')?.addEventListener('click', updateWeekView);

export async function updateWeekView() {
  const weekContainer = document.getElementById('week-container');

  if (!weekContainer) {
    console.error("Week container not found");
    return;
  }

  weekContainer.innerHTML = '';

  let jobs;
  try {
    jobs = await getJobDataIndex();
    if (!Array.isArray(jobs)) {
      console.error("getJobDataIndex did not return an array");
      return;
    }
  } catch (error) {
    console.error("Error fetching job data:", error);
    return;
  }

 /* const techNames = handleTechNames(shell.globalUserData) */
  const techStats = initializeTechStats(shell.globalUserData);


  try {
    const includeSaturday = isSaturdayIncluded(jobs);
    const { weekStart, weekEnd } = getCurrentWeekRange(includeSaturday);


    await createHeaderRow(weekStart, weekEnd);

    jobs = processJobs(jobs, getTodayString());
    await distributeAndSortJobs(jobs, techStats, weekStart, weekEnd);
    globalTechStats = techStats;
    const updatedTechList = Object.keys(techStats);
    for (const tech of updatedTechList) {
      if (tech && tech !== '') {
        await createTechRow(tech, techStats, weekContainer, weekStart);
      }
    }

    adjustColumnWidths(weekContainer);
    addDragAndDropListeners();
    updateWeekLabel(weekStart, weekEnd);

  } catch (e) {
    console.error("Error in updateWeekView:", e);
    console.error("Stack trace:", e.stack);
  }
}

function getCurrentWeekRange() {
  const weekStart = new Date(currentWeekStart);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 5);
  return { weekStart, weekEnd };
}

function initializeTechStats(userData) {

  let techList = [];
  let techIds = {};
  
  const techToday = getTodayString();
  
  Object.values(userData).forEach(tech => {

    const roleTokens = (tech.role || '').split(',').map(s => s.trim().toLowerCase());
    if(roleTokens.includes('tech')){
      techList.push(tech.displayName.split(' ')[0]);
      if(!techIds[tech.displayName.split(' ')[0]]){
        techIds[tech.displayName.split(' ')[0]] = {id: tech.id, skills: tech.skills, role: tech.role};
      }
    }
  });

  // Sort the techList alphabetically
  techList.sort((a, b) => a.localeCompare(b));

  const techStats = {};
  techList.forEach(tech => {
    techStats[tech] = {
      id: techIds[tech].id, 
      totalHours: 0, 
      skills: techIds[tech].skills,
      role: techIds[tech].role,
      dailyStats: {},
      availableDay: techToday.split('-')[1] + '/' + techToday.split('-')[2] 
    };
  });

  return techStats;
}

function getTodayString() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.toISOString().split('T')[0];
}

async function createHeaderRow(weekStart, weekEnd) {

  document.getElementById('weekdays').innerHTML = '';

  const headerRow = document.createElement('div');
  headerRow.className = 'header-row';

  const techHeader = document.createElement('div');
  techHeader.className = 'tech-header';
  techHeader.textContent = 'TECH';
  headerRow.appendChild(techHeader);

  let numberOfDays = 6; // Default to 5 days

  /*   if (isSaturdayIncluded(jobs)) {
      console.log("Saturday is included in the jobs list");
        numberOfDays = 6; // Extend to include Saturday
    } */

  for (let i = 0; i < numberOfDays; i++) {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    const dateString = date.toISOString().split('T')[0];

    let weatherInfo;
    try {
      [weatherInfo] = await Promise.all([
        fetchWeatherInfo(dateString)
      ]);
    } catch (error) {
      console.warn(`Error fetching tide or weather info for ${dateString}:`, error);
    }

    const dateHeader = document.createElement('div');
    dateHeader.className = 'date-header';
    dateHeader.setAttribute('data-date', dateString);
    dateHeader.innerHTML = `
          ${weekday[date.getDay()].slice(0, 3).toUpperCase()}<br>
          ${formatDateWithOrdinal(date)}<br>
          ${weatherInfo ? getWeatherEmoji(weatherInfo.description) : ''}
      `;
    headerRow.appendChild(dateHeader);
  }

  document.getElementById('weekdays').appendChild(headerRow);
}

function isSaturdayIncluded(jobs) {
  return jobs.some(job => {
    const jobDate = new Date(job.Schedule_Date);
    return jobDate.getUTCDay() === 6; // Saturday
  });
}

function processJobs(jobs, todayString) {
  return jobs
    .filter(job => (job.Status === "On Work Schedule" || job.Status === "In Progress") && (job.Techs || '').trim() !== '')
    .sort((a, b) => new Date(a.Start_Date) - new Date(b.Start_Date))
    .map(job => {
      if (new Date(job.Schedule_Date) < new Date(todayString)) {
        job.Schedule_Date = todayString;
        job.Sort_Date = todayString;
      } else {
        job.Sort_Date = job.Schedule_Date;
      }
      return job;
    });
}

async function distributeAndSortJobs(jobs, techStats, weekStart, weekEnd) {
  const startOfWeek = new Date(weekStart);
  const endOfWeek = new Date(weekEnd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yardTechs = Object.keys(techStats).filter(tech => techStats[tech].role.includes('yard'));

  const techToday = getTodayString();

  let allTechs = {};

  function isValidDate(d) {
    return d instanceof Date && !isNaN(d);
  }

  // Initialize allTechs and techStats
  for (const job of jobs) {

    let techNames = job.Techs.split(',').map(tech => tech.trim());

    if(techNames.includes('Yard')){
      techNames = yardTechs;
    }

    techNames.forEach(tech => {
      if (!allTechs[tech]) {
        const scheduleDate = new Date(job.Schedule_Date);
        allTechs[tech] = {
          totalHours: 0,
          currentDate: isValidDate(scheduleDate) ? new Date(Math.max(today, scheduleDate)) : new Date(today)
        };
      }
      if (!techStats[tech]) {
        techStats[tech] = { totalHours: 0, dailyStats: {}, availableDay: techToday.split('-')[1] + '/' + techToday.split('-')[2] };
      }
    });
  }

  // Process jobs with a Schedule_Date first
  const scheduledJobs = jobs.filter(job => isValidDate(new Date(job.Schedule_Date)));
  const unscheduledJobs = jobs.filter(job => !isValidDate(new Date(job.Schedule_Date)));

  for (const job of [...scheduledJobs, ...unscheduledJobs]) {

    let techNames = job.Techs.split(',').map(tech => tech.trim());

    if(techNames.includes('Yard')){
      techNames = yardTechs;
    }
    
    let remainingHours = job.Estimated_Hours ? parseFloat(job.Estimated_Hours) : 0;
    let jobScheduleDate = new Date(job.Schedule_Date);
    let jobStartDate = isValidDate(jobScheduleDate) ? new Date(Math.max(today, jobScheduleDate)) : new Date(today);

    while (remainingHours > 0) {
      let earliestDate = new Date(Math.max(jobStartDate, ...techNames.map(tech => allTechs[tech].currentDate)));
      let dateString = earliestDate.toISOString().split('T')[0];

      // Calculate available hours for the day
      let availableHours = Math.min(8, ...techNames.map(tech => 8 - (techStats[tech].dailyStats[dateString]?.hours || 0)));
      let hoursToAdd = Math.min(remainingHours, availableHours);

      if (hoursToAdd <= 0) {
        // If no hours are available, push to the next day
        earliestDate.setDate(earliestDate.getDate() + 1);
        while (earliestDate.getDay() === 0 || earliestDate.getDay() === 6) {
          earliestDate.setDate(earliestDate.getDate() + 1);
        }
        jobStartDate = new Date(earliestDate);
        continue;
      }

      try {
        // Only process if the date is within the current week
        
          techNames.forEach(tech => {
            if (!techStats[tech].dailyStats[dateString]) {
              techStats[tech].dailyStats[dateString] = { hours: 0, jobCount: 0, jobs: [] };
            }

            // Prioritize scheduled jobs
            if (scheduledJobs.includes(job)) {
              techStats[tech].dailyStats[dateString].jobs.unshift({
                ...job,
                Estimated_Hours: hoursToAdd,
                Sort_Date: dateString
              });
            } else {
              techStats[tech].dailyStats[dateString].jobs.push({
                ...job,
                Estimated_Hours: hoursToAdd,
                Sort_Date: dateString
              });
            }

            techStats[tech].dailyStats[dateString].hours += hoursToAdd;
            techStats[tech].dailyStats[dateString].jobCount++;
            techStats[tech].totalHours += hoursToAdd;
            techStats[tech].availableDay = dateString.split('-')[1] + '/' + dateString.split('-')[2];
          });
        

        techNames.forEach(tech => {
          allTechs[tech].totalHours += hoursToAdd;
          if (allTechs[tech].totalHours >= 8) {
            allTechs[tech].totalHours -= 8;
            do {
              allTechs[tech].currentDate.setDate(allTechs[tech].currentDate.getDate() + 1);
            } while (allTechs[tech].currentDate.getDay() === 0 || allTechs[tech].currentDate.getDay() === 6);
          }
        });

        remainingHours -= hoursToAdd;

        if (remainingHours > 0) {
          earliestDate.setDate(earliestDate.getDate() + 1);
          while (earliestDate.getDay() === 0 || earliestDate.getDay() === 6) {
            earliestDate.setDate(earliestDate.getDate() + 1);
          }
          jobStartDate = new Date(earliestDate);
        }
      } catch (error) {
        console.error("Error processing job:", job);
        console.error("Error details:", error);
        break;
      }
    }
  }

  globalTechList = [];
  Object.keys(techStats).forEach(tech => {
    globalTechList.push(tech + ": " + techStats[tech].availableDay);
  });

  window.globalTechList = globalTechList;

  setTechSelects(globalTechList);

  return techStats;
}

// The row/button that opened the panel gets '.current-tech-select' on
// whichever child element the click actually landed on (e.target, not the
// button itself — see populateTechSelectModal below). writeBackAssignedTechs()
// resolves that back up to the real label element regardless of which one it
// was, instead of blindly overwriting e.target's own textContent: the compact
// row's tech button (.op-row-tech-label) and the Operations-tab flat table's
// (.op-row-tech-name) are two different structures, and a naive
// `.textContent =` on whatever got tagged would wipe out the sibling chevron
// span whenever the click landed on the button's own padding rather than a
// child span.
function writeBackAssignedTechs(text, hasTechs) {
  const tagged = document.querySelector('.current-tech-select');
  if (!tagged) return;
  const techButton = tagged.closest('.op-row-tech.op-tech-select') || tagged;
  const label = techButton.querySelector('.op-row-tech-label, .op-row-tech-name');
  if (label) {
    label.textContent = text;
  } else {
    techButton.textContent = text;
  }
  techButton.classList.toggle('op-tech-unassigned', !hasTechs);
  tagged.classList.remove('current-tech-select');
}

// Recomputes the selected-chips row and the primary button's label/disabled
// state from the checkboxes' checked state (source of truth, per the
// design handoff) and the pinned yard row's .selected class.
function refreshTechSelectSummary() {
  const modal = document.getElementById('techSelectModal');
  const chipsContainer = document.getElementById('techSelectChips');
  const assignBtn = document.getElementById('techAssignButton');
  const yardRow = modal.querySelector('.tech-yard-row');
  const isYardSelected = !!(yardRow && yardRow.classList.contains('selected'));

  const selectedNames = Array.from(modal.querySelectorAll('.tech-checkbox'))
    .filter(cb => cb.checked)
    .map(cb => cb.value);

  chipsContainer.innerHTML = selectedNames.map(name => {
    const { initials, color } = techAvatar(name, shell.getTechId);
    return `<span class="tech-selected-chip" data-tech="${escapeHTML(name)}"><span class="tech-selected-chip-avatar" style="background:${color};">${escapeHTML(initials)}</span>${escapeHTML(name)}<span class="tech-selected-chip-remove" title="Remove">&times;</span></span>`;
  }).join('');
  chipsContainer.querySelectorAll('.tech-selected-chip-remove').forEach(removeEl => {
    removeEl.addEventListener('click', (evt) => {
      evt.stopPropagation();
      const name = removeEl.closest('.tech-selected-chip').getAttribute('data-tech');
      const cb = modal.querySelector(`.tech-checkbox[value="${CSS.escape(name)}"]`);
      if (cb) {
        cb.checked = false;
        const row = cb.closest('.tech-row');
        if (row) row.classList.remove('selected');
      }
      refreshTechSelectSummary();
    });
  });

  if (isYardSelected) {
    assignBtn.textContent = 'Assign to Yard';
    assignBtn.disabled = false;
  } else if (selectedNames.length > 0) {
    const hoursVal = parseFloat(document.getElementById('techSelectEstimatedHours').value) || 0;
    assignBtn.textContent = `Assign ${selectedNames.length} tech${selectedNames.length === 1 ? '' : 's'} · ${hoursVal.toFixed(1)} hrs`;
    assignBtn.disabled = false;
  } else {
    assignBtn.textContent = 'Assign Techs';
    assignBtn.disabled = true;
  }
}

export async function populateTechSelectModal(e, view) {

  e.target.classList.add('current-tech-select');

  let operationData;

  if(view === 'table'){
    operationData = JSON.parse(e.target.closest('.op-table-data-row').getAttribute('data-jobinfo'));
  }else{
    operationData = JSON.parse(e.target.closest('.job-item').getAttribute('data-jobinfo'));
  }

  document.getElementById('currentTechSelectJobId').value = operationData.Job_ID;

  const currentTechs = operationData.Techs ? operationData.Techs.split(', ') : [];
  const isYard = currentTechs.includes('Yard');

  // #yardAssignButton stays permanently hidden (see its CSS) — its job moved
  // onto the pinned .tech-yard-row + the primary button's dynamic label.
  document.getElementById('yardRemoveButton').style.display = isYard ? 'flex' : 'none';

  const jobData = await getJobDataIndex();

  const techList = globalTechList;
  const techContainer = document.getElementById('techSelectBody');
  techContainer.innerHTML = '';

  const estimatedHours = operationData.Estimated_Hours || 0;
  document.getElementById('techSelectEstimatedHours').value = estimatedHours;
  document.getElementById('techSelectEstimatedHours').oninput = refreshTechSelectSummary;

  const opShortDesc = (operationData.Short_Description_DM || operationData.Description || '').split(':')[0].split('(')[0];
  const contextParts = [
    [operationData.OpCode, opShortDesc].filter(Boolean).join(' '),
    operationData.Work_Order ? woLabel(operationData.Work_Order) : '',
    operationData.Boat_Name || ''
  ].filter(Boolean);
  document.getElementById('techSelectOpContext').textContent = contextParts.join(' · ');

  document.getElementById('techSelectModal').classList.add('active');

  // Day load per tech — "N ops today · H.H hrs", from jobs sharing this op's
  // Schedule_Date. jobData is already fetched above; only meaningful once the
  // op has a Schedule_Date, so the line is simply omitted otherwise (no
  // anchor date to count "today" against).
  const scheduleDate = operationData.Schedule_Date;
  const loadByTech = {};
  if (scheduleDate) {
    const DONE_OR_DEAD = ['Declined', 'Complete', 'Closed', 'Ready to Bill'];
    jobData.forEach(job => {
      if (job.Schedule_Date !== scheduleDate) return;
      if (DONE_OR_DEAD.includes(job.Status)) return;
      splitTechs(job.Techs).forEach(name => {
        if (!loadByTech[name]) loadByTech[name] = { count: 0, hours: 0 };
        loadByTech[name].count += 1;
        loadByTech[name].hours += parseFloat(job.Estimated_Hours) || 0;
      });
    });
  }

  const selectedSet = new Set(currentTechs.filter(t => t !== 'Yard'));

  // Yard — its own pinned choice, above the list, mutually exclusive with
  // named-tech selection.
  const yardRow = document.createElement('div');
  yardRow.className = 'tech-yard-row';
  if (isYard) yardRow.classList.add('selected');
  yardRow.innerHTML = `
    <span class="tech-yard-icon">Y</span>
    <div class="tech-row-info">
      <div class="tech-name">Yard queue</div>
      <div class="tech-load">General yard work, no named tech</div>
    </div>
    <span class="tech-check">&#10003;</span>
  `;
  yardRow.addEventListener('click', () => {
    const nowSelected = !yardRow.classList.contains('selected');
    yardRow.classList.toggle('selected', nowSelected);
    if (nowSelected) {
      techContainer.querySelectorAll('.tech-checkbox').forEach(cb => { cb.checked = false; });
      techContainer.querySelectorAll('.tech-row.selected').forEach(row => row.classList.remove('selected'));
    }
    refreshTechSelectSummary();
  });
  techContainer.appendChild(yardRow);

  const list = document.createElement('div');
  list.className = 'tech-select-list';

  techList.forEach(tech => {
    const techName = tech.split(':')[0];
    const isSelected = selectedSet.has(techName);

    const techRow = document.createElement('div');
    techRow.className = 'tech-row';
    if (isSelected) techRow.classList.add('selected');

    const { initials, color } = techAvatar(techName, shell.getTechId);
    const load = loadByTech[techName];
    const loadHtml = (scheduleDate && load)
      ? `<div class="tech-load">${load.count} op${load.count === 1 ? '' : 's'} today · ${load.hours.toFixed(1)} hrs</div>`
      : '';

    techRow.innerHTML = `
      <input type="checkbox" class="tech-checkbox" value="${escapeHTML(techName)}" ${isSelected ? 'checked' : ''}>
      <span class="tech-avatar" style="background:${color};">${escapeHTML(initials)}</span>
      <div class="tech-row-info">
        <div class="tech-name">${escapeHTML(techLabel(techName))}</div>
        ${loadHtml}
      </div>
      <span class="tech-check">&#10003;</span>
    `;

    const techCheckbox = techRow.querySelector('.tech-checkbox');

    techRow.addEventListener('click', (evt) => {
      evt.preventDefault();
      techCheckbox.checked = !techCheckbox.checked;
      techRow.classList.toggle('selected', techCheckbox.checked);
      if (techCheckbox.checked) {
        yardRow.classList.remove('selected');
      }
      refreshTechSelectSummary();
    });

    list.appendChild(techRow);
  });

  techContainer.appendChild(list);

  const filterInput = document.getElementById('techSelectFilter');
  filterInput.value = '';
  filterInput.oninput = () => {
    const q = filterInput.value.trim().toLowerCase();
    list.querySelectorAll('.tech-row').forEach(row => {
      const name = row.querySelector('.tech-name').textContent.toLowerCase();
      row.style.display = name.includes(q) ? '' : 'none';
    });
  };

  refreshTechSelectSummary();
}

document.getElementById('techAssignButton').addEventListener('click', () => {
  const yardRow = document.querySelector('#techSelectModal .tech-yard-row');
  if (yardRow && yardRow.classList.contains('selected')) {
    assignYardToJob();
  } else {
    assignTechsToJob();
  }
});

async function assignTechsToJob() {

  const techs = document.querySelectorAll('.tech-checkbox');
  const jobId = document.getElementById('currentTechSelectJobId').value;
  const estimatedHours = document.getElementById('techSelectEstimatedHours').value;

  if(estimatedHours <= 0 || estimatedHours == ''){
    toastMessage('Estimated hours required','red');
    return;
  }

  const selectedTechs = Array.from(techs).filter(tech => tech.checked).map(tech => tech.value);

  const jobRef = ref(database, `/data/${jobId}`);
  await update(jobRef, {'Techs': selectedTechs.join(', '), 'Estimated_Hours': estimatedHours});
  // Bumps /data/lastUpdated, which an open Scheduler polls to pick up the new assignment.
  updateTimestamps('jobData', 'data');

  writeBackAssignedTechs(selectedTechs.join(', '), selectedTechs.length > 0);
  document.getElementById('techSelectModal').classList.remove('active')

};

async function assignYardToJob() {

  const jobId = document.getElementById('currentTechSelectJobId').value;
  const estimatedHours = document.getElementById('techSelectEstimatedHours').value;

  if(estimatedHours <= 0 || estimatedHours == ''){
    toastMessage('Estimated hours required','red');
    return;
  }

  const jobRef = ref(database, `/data/${jobId}`);
  await update(jobRef, {'Techs': 'Yard', 'Estimated_Hours': estimatedHours});
  updateTimestamps('jobData', 'data');

  writeBackAssignedTechs('Yard', true);
  document.getElementById('techSelectModal').classList.remove('active')

};

document.getElementById('yardRemoveButton').addEventListener('click', removeYardFromJob);

async function removeYardFromJob() {
  const jobId = document.getElementById('currentTechSelectJobId').value;

  const jobRef = ref(database, `/data/${jobId}`);
  const jobSnapshot = await get(jobRef);

  if (!jobSnapshot.exists()) {
    toastMessage('Job not found', 'red');
    return;
  }

  let techs = jobSnapshot.val().Techs || '';

  let updatedTechs = techs.split(', ')
                          .filter(tech => tech.trim() !== 'Yard')
                          .join(', ');

  if (updatedTechs.trim() === '') {
    updatedTechs = '';
  }

  await update(jobRef, {'Techs': updatedTechs});
  updateTimestamps('jobData', 'data');

  writeBackAssignedTechs(updatedTechs, updatedTechs.trim() !== '');
  document.getElementById('techSelectModal').classList.remove('active');
}


async function setTechSelects(techList) {

  const laborDropdown = document.getElementById('labor-entry-tech');
  laborDropdown.innerHTML = '';

  const partDropdown = document.getElementById('part-entry-tech');
  partDropdown.innerHTML = '';

  const partAddDropdown = document.getElementById('part-add-tech');
  if (partAddDropdown) partAddDropdown.innerHTML = '';

  const techNames = techList;
  techNames.forEach(name => {
    const optionElement = document.createElement('option');
    optionElement.value = name.split(':')[0];
    optionElement.textContent = name.split(':')[0];
    laborDropdown.appendChild(optionElement);
    partDropdown.appendChild( optionElement.cloneNode(true) );
    if (partAddDropdown) partAddDropdown.appendChild(optionElement.cloneNode(true));
  });

}

async function createTechRow(tech, techStats, weekContainer, weekStart) {

  const techRow = document.createElement('div');
  techRow.className = 'tech-row';

  const techNameColumn = document.createElement('div');
  techNameColumn.className = 'tech-stats';

  const techName = document.createElement('div');
  techName.className = 'tech-name';
  techName.textContent = tech;

  techNameColumn.appendChild(techName);

  const progressBarContainer = document.createElement('div');
  progressBarContainer.className = 'progress-bar-container';

  const progressWrapper = document.createElement('div');
  progressWrapper.className = 'progress-wrapper';

  const progressBar = document.createElement('div');
  progressBar.className = 'progress-bar';
  progressBar.id = `${tech}-progress-bar`;

  progressWrapper.appendChild(progressBar);

  const techHours = document.createElement('div');
  techHours.className = 'tech-hours';
  techHours.id = `${tech}-hours`;

  progressBarContainer.appendChild(progressWrapper);
  progressBarContainer.appendChild(techHours);
  techNameColumn.appendChild(progressBarContainer);
  techRow.appendChild(techNameColumn);

  const numberOfDays = 6;
  let numberOfHours = 40;
  let totalRowHours = 0;

  for (let i = 0; i < numberOfDays; i++) {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);

    const dateString = await shell.getFormattedDate(date); // Use the provided async function for formatted date

    const dayColumn = document.createElement('div');
    dayColumn.className = 'day-column';
    dayColumn.setAttribute('data-date', dateString);
    dayColumn.setAttribute('data-tech', tech);

    const eventContainer = document.createElement('div');
    eventContainer.className = 'event-container';

    const dayStats = techStats[tech].dailyStats[dateString] || { hours: 0, jobCount: 0, jobs: [] };

    dayStats.jobs.forEach(job => {
      try {

        if (parseDay(job.Schedule_Date).getDay() == 6 && numberOfHours == 40) {
          numberOfHours = 48;
        }

        const eventElement = createEventElement(job);
        eventContainer.appendChild(eventElement);
      } catch (error) {
        console.error(`Error creating event element for job:`, job, error);
      }
    });

    const countElement = document.createElement('div');
    countElement.className = 'count-element';
    if (dayStats.hours == 1 && dayStats.jobCount == 1) {
      countElement.textContent = `${dayStats.jobCount} JOB / ${dayStats.hours.toFixed(1)} HR`;
    } else if (dayStats.hours == 1 && dayStats.jobCount != 1) {
      countElement.textContent = `${dayStats.jobCount} JOBS / ${dayStats.hours.toFixed(1)} HR`;
    } else if (dayStats.hours != 1 && dayStats.jobCount == 1) {
      countElement.textContent = `${dayStats.jobCount} JOB / ${dayStats.hours.toFixed(1)} HRS`;
    } else {
      countElement.textContent = `${dayStats.jobCount} JOBS / ${dayStats.hours.toFixed(1)} HRS`;
    }

    totalRowHours += dayStats.hours;

    dayColumn.appendChild(eventContainer);
    dayColumn.appendChild(countElement);
    techRow.appendChild(dayColumn);
  }

  weekContainer.appendChild(techRow);

  let currentDate = new Date();
  let currentWeekStartDate = new Date(currentWeekStart);
  let remainingHours;

  // Check if the current date is within the same week as currentWeekStart
  if (currentDate >= currentWeekStartDate && currentDate < new Date(currentWeekStartDate.getTime() + 7 * 24 * 60 * 60 * 1000)) {
    let daysPassed = currentDate.getDay() - currentWeekStartDate.getDay();

    if (currentDate.getDay() === 0) {  // Sunday case
      daysPassed = 6 * 8; // Consider the whole week passed
    } else if (currentDate.getDay() === 1) { // Monday case
      daysPassed = 0; // No hours have passed yet
    } else {
      daysPassed = (currentDate.getDay() - 1) * 8;
    }

    remainingHours = Math.max(0, numberOfHours - daysPassed);
  } else {
    // If the current date is not within the current week, assume the full week is remaining
    remainingHours = numberOfHours;
  }



  const totalHours = totalRowHours;
  const progressBarWidth = (totalHours / remainingHours) * 100;

  const progressBarElement = document.getElementById(`${tech}-progress-bar`);
  if (progressBarElement) {
    progressBarElement.style.width = `${progressBarWidth}%`;
  } else {
    console.warn(`Progress bar element not found for tech: ${tech}`);
  }

  const techHoursElement = document.getElementById(`${tech}-hours`);
  if (techHoursElement) {
    if (totalHours == 1) {
      techHoursElement.textContent = `${totalHours.toFixed(1)} HOUR`;
    } else {
      techHoursElement.textContent = `${totalHours.toFixed(1)} HOURS`;
    }
  } else {
    console.warn(`Tech hours element not found for tech: ${tech}`);
  }
}

function adjustColumnWidths(weekContainer) {
  const allDayColumns = weekContainer.querySelectorAll('.day-column');
  const headerColumns = weekContainer.querySelectorAll('.date-header');
  if (headerColumns.length > 0) {
    const columnWidth = headerColumns[0].offsetWidth;
    allDayColumns.forEach(column => {
      column.style.width = `${columnWidth}px`;
    });
  } else {
    console.warn("No header columns found");
  }
}

function createEventElement(event) {
  const eventElement = document.createElement('div');
  eventElement.className = 'event';
  eventElement.draggable = true;
  eventElement.setAttribute('data-event-id', event.Job_ID || 'unknown');

  const customerDescriptionDiv = document.createElement('div');
  customerDescriptionDiv.className = 'customer-description';

  const customer = document.createElement('div');
  customer.className = 'schedule-customer';
  customer.textContent = `${event.Customer_Name.split(',')[0] || 'Unknown'} / ${event.Boat_Name || 'Unknown'}`;

  const description = document.createElement('div');
  description.className = 'schedule-description';
  description.textContent = event.Description || 'No description';

  const hours = document.createElement('div');
  hours.className = 'schedule-hours';
  const estimatedHours = parseFloat(event.Estimated_Hours) || 0;
  hours.textContent = estimatedHours === 1 ? "1 HR" : `${estimatedHours} HRS`;

  customerDescriptionDiv.appendChild(description);
  customerDescriptionDiv.appendChild(customer);
  eventElement.appendChild(customerDescriptionDiv);

  if (estimatedHours > 0) {
    eventElement.appendChild(hours);
  } else {
    eventElement.style.display = 'none';
  }

  return eventElement;
}

function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return day + 'th'; // Covers 11th through 20th
  switch (day % 10) {
    case 1: return day + 'st';
    case 2: return day + 'nd';
    case 3: return day + 'rd';
    default: return day + 'th';
  }
}

function formatDateWithOrdinal(date) {
  const day = date.getDate();
  return `${getOrdinalSuffix(day)}`;
}

function addEvent(date, description) {
  shell.events.push({ date, description });
  updateWeekView();
}

export async function load() {
  try {
   await updateWeekView();

    document.getElementById('schedule-prev-week').addEventListener('click', () => navigateWeek(-1));
    document.getElementById('schedule-next-week').addEventListener('click', () => navigateWeek(1));


    if (document.getElementById('wrap')) {
      var sort = new Sort("wrap");
      sort.init();
    } else {
      console.warn("Element with id 'wrap' not found. Sort initialization skipped.");
    }
  } catch (e) {
    console.log(e)
  }
}

function addDragAndDropListeners() {

  const events = document.querySelectorAll('.event');
  const dayColumns = document.querySelectorAll('.day-column');

  events.forEach(event => {
    event.addEventListener('dragstart', dragStart);
    event.addEventListener('dragend', dragEnd);
  });

  dayColumns.forEach(column => {
    column.addEventListener('dragover', dragOver);
    column.addEventListener('dragenter', dragEnter);
    column.addEventListener('dragleave', dragLeave);
    column.addEventListener('drop', drop);
  });
}

function dragStart(e) {
  e.dataTransfer.setData('text/plain', e.target.getAttribute('data-event-id'));
  setTimeout(() => e.target.style.opacity = '0.5', 0); // Adding opacity for visual feedback
}

function dragEnd(e) {
  e.target.style.opacity = '1'; // Reset opacity after drag ends
}

function dragOver(e) {
  e.preventDefault(); // Ensure the drop is allowed
  e.dataTransfer.dropEffect = 'move'; // Indicates a move operation
}

function dragEnter(e) {
  e.preventDefault(); // Ensure the drop is allowed
  if (e.target.classList.contains('day-column') || e.target.classList.contains('event-container')) {
    e.target.classList.add('drag-over');
  }
}

function dragLeave(e) {
  if (e.target.classList.contains('day-column') || e.target.classList.contains('event-container')) {
    e.target.classList.remove('drag-over');
  }
}

function drop(e) {
  e.preventDefault();

  const eventId = e.dataTransfer.getData('text');
  const draggedElement = document.querySelector(`[data-event-id="${eventId}"]`);
  const dropZone = e.target.closest('.day-column');
  const newTech = dropZone.getAttribute('data-tech');
  const newDate = dropZone.getAttribute('data-date');

  if (!draggedElement || !dropZone || !newTech || !newDate) {
    console.error('Drag and drop failed due to missing elements or attributes.');
    return;
  }

  // Get original tech and date before the move
  const originalTech = draggedElement.closest('.day-column').getAttribute('data-tech');
  const originalDate = draggedElement.closest('.day-column').getAttribute('data-date');

  // Append the dragged event to the new event container
  dropZone.querySelector('.event-container').appendChild(draggedElement);

  // Update the event's schedule date and tech in the database
  updateEventDate(eventId, newDate, newTech)
    .then(() => {
      // Recalculate hours for the new day column
      updateDayStats(newTech, newDate);

      // Recalculate hours for the original day column
      updateDayStats(originalTech, originalDate);

      // Update the progress bar and total hours for both the original and new techs
      updateTechProgressBar(originalTech);
      updateTechProgressBar(newTech);
      shell.updateJobInfo(eventId, "Techs", newTech);
      shell.updateJobInfo(eventId, "Schedule_Date", newDate);
    })
    .catch(error => {
      console.error('Failed to update event date and tech:', error);
    });
}

function updateTechProgressBar(tech) {

  const techRow = document.querySelector(`.tech-row[data-tech="${tech}"]`);
  if (!techRow) return;

  const techStats = getTechStatsForTech(tech); // Assume you have a way to retrieve the latest stats for a tech
  const progressBar = techRow.querySelector('.progress-bar');
  const techHoursElement = techRow.querySelector('.tech-hours');

  const numberOfHours = techStats.includesSaturday ? 48 : 40;
  const progressBarWidth = (techStats.totalHours / numberOfHours) * 100;

  if (progressBar) {
    progressBar.style.width = `${progressBarWidth}%`;
  }

  if (techHoursElement) {
    techHoursElement.textContent = `${techStats.totalHours.toFixed(1)} HOUR${techStats.totalHours === 1 ? '' : 'S'}`;
  }
}

function getTechStatsForTech(tech) {
  // Logic to retrieve the latest stats for the given tech
  return (globalTechStats || {})[tech] || { totalHours: 0, includesSaturday: false };
}


function updateDayStats(tech, date) {
  const dayColumn = document.querySelector(`.day-column[data-tech="${tech}"][data-date="${date}"]`);
  if (!dayColumn) return;

  const events = dayColumn.querySelectorAll('.event');
  let totalHours = 0;
  events.forEach(event => {
    const hoursText = event.querySelector('.schedule-hours')?.textContent;
    if (hoursText) {
      totalHours += parseFloat(hoursText.replace(' HR', '').replace(' HRS', ''));
    }
  });

  const countElement = dayColumn.querySelector('.count-element');
  if (countElement) {
    countElement.textContent = `${events.length} JOB${events.length === 1 ? '' : 'S'} / ${totalHours.toFixed(1)} HR${totalHours === 1 ? '' : 'S'}`;
  } else {
    console.warn('Count element not found for day column:', dayColumn);
  }
}

function updateWeekLabel(weekStart) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 5);
  const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  document.getElementById('current-week').textContent = `${startStr} - ${endStr}`;
}

async function updateEventDate(eventId, newDate, tech) {

  try {
    await shell.updateJobInfo(eventId, "Schedule_Date", newDate);
    await shell.updateJobInfo(eventId, "Techs", tech);
  } catch (error) {
    console.log('Error updating event data', error);
  };

}
